import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Button } from './ui/index.ts'
import { authClient } from '../lib/auth-client.ts'

/**
 * The account menu, and the app's only way out of a session.
 *
 * There was no sign-out control anywhere before this. The only way to drop
 * a session was to clear the cookie by hand, which on a shared operator
 * box is how one person's session ends up doing another person's work.
 *
 * It is a plain disclosure button and a `role="menu"` panel rather than a
 * library popover: one item, no submenus, no positioning maths. Escape
 * closes it and focus returns to the trigger; a pointer press anywhere
 * outside closes it too. Arrow-key roving is deliberately not implemented —
 * with a single `menuitem` there is nothing to rove between, and a
 * half-implemented keyboard contract is worse than an honest one.
 */

/** Pulls a human-readable message out of better-auth's `{ data, error }`
 * envelope without asserting its shape. The client resolves rather than
 * rejects on an auth failure, so a bare `await` would report success. */
function signOutErrorMessage(result: unknown): string | null {
  if (typeof result !== 'object' || result === null || !('error' in result))
    return null
  const { error } = result
  if (error === null || error === undefined) return null
  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  )
    return error.message
  return 'Sign-out failed'
}

export function AccountMenu(): ReactElement {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen) return

    // The trigger is found in the DOM rather than held in a ref because
    // `Button` deliberately publishes no `ref` prop — its props are
    // `ComponentPropsWithoutRef`, so no caller can reach past the token
    // layer. The selector is the trigger's own ARIA contract, so it cannot
    // drift the way a class name would.
    const focusTrigger = (): void => {
      const trigger = containerRef.current?.querySelector(
        '[aria-haspopup="menu"]',
      )
      if (trigger instanceof HTMLElement) trigger.focus()
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      setIsOpen(false)
      // Focus goes back to the trigger, not to the document: a keyboard
      // user who escapes a menu and lands at the top of the page has lost
      // their place.
      focusTrigger()
    }
    // `pointerdown`, not `click`: closing on click means the press that
    // dismisses the menu also activates whatever was under it, which on
    // this header is the theme toggle or the Lock button.
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target
      if (target instanceof Node && containerRef.current?.contains(target))
        return
      setIsOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [isOpen])

  async function signOut(): Promise<void> {
    setIsBusy(true)
    setError(null)
    try {
      const message = signOutErrorMessage(await authClient.signOut())
      if (message !== null) {
        setError(message)
        return
      }
      setIsOpen(false)
      await navigate({ to: '/login' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-out failed')
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <Button
        variant="ghost"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => {
          setIsOpen(!isOpen)
        }}
      >
        Account
      </Button>
      {isOpen && (
        <div
          role="menu"
          aria-label="Account"
          // `bg-elevated` plus a 1px border is how this app does elevation.
          // A drop shadow is invisible against a near-black canvas, so the
          // popover would read as painted onto the header instead of over
          // it. See the surface ramp in styles.css.
          className="absolute right-0 z-20 mt-1 w-48 rounded-md border border-border bg-elevated p-1 text-sm"
        >
          <button
            type="button"
            role="menuitem"
            disabled={isBusy}
            onClick={() => {
              void signOut()
            }}
            className="flex min-h-row w-full items-center rounded-sm px-2 text-left text-fg transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50"
          >
            Sign out
          </button>
          {error !== null && (
            <p
              role="alert"
              className="mt-1 px-2 py-1 text-xs text-status-danger-fg"
            >
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
